import { CommonLoader, PortfolioSelection, PortfolioValue, UnableToLoadData } from '@components/index';
import PageHeader from '@components/pageHeader';
import { DonutLarge } from '@mui/icons-material';
import { Box, Container, Divider, Grid, useMediaQuery, useTheme } from '@mui/material';
import { ScreenState } from '@src/app/enums';
import { useAppDispatch, useAppSelector } from '@src/app/hooks/store';
import { selectAssetAllocation } from '@src/app/store/reducers/assetAllocation/selectors';
import { getAssetAllocationAsync } from '@src/app/store/reducers/assetAllocation/thunks';
import { FC, useEffect } from 'react';
import AssetCardContainer from './AssetCardContainer';
import { selectPortfolios } from '@src/app/store/reducers/portfolios/selectors';
import { selectHoldings } from '@src/app/store/reducers/holdings/selectors';
import { setAsOfDate } from '@src/app/store/reducers/holdings';
import DoughnutChart from './DoughnutChart';
import { useWindow } from '@src/app/hooks/useWindow';
import { DateRange } from '@src/app/store';
import DateRangePicker from '@components/dateRange';
import { LocalizationProvider } from '@mui/lab';
import AdapterLuxon from '@mui/lab/AdapterLuxon';

const AssetAllocation: FC = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const theme = useTheme();

  const { resizing } = useWindow();
  const { portfoliosSelected, portfolioInfoState } = useAppSelector(selectPortfolios);
  const { asOfDate } = useAppSelector(selectHoldings);
  const { view, state, totalValue } = useAppSelector(selectAssetAllocation);

  const isBetweenXsAndSm = useMediaQuery(theme.breakpoints.between('xs', 'sm'));

  const setDate = (date: DateRange) => {
    dispatch(setAsOfDate(date));
  };

  const fetchAssetAllocationAsync = async () => {
    await dispatch(
      getAssetAllocationAsync({
        asOfDate: asOfDate.dateTo,
        clientPortfolios: portfoliosSelected,
      })
    );
  };

  useEffect(() => {
    if (state !== ScreenState.SuccessDataLoaded) {
      fetchAssetAllocationAsync();
    }
  }, []);

  useEffect(() => {
    if (state !== ScreenState.Loading) {
      fetchAssetAllocationAsync();
    }
  }, [asOfDate, portfoliosSelected]);

  if (state === ScreenState.Loading || portfolioInfoState === ScreenState.Loading) {
    return <CommonLoader />;
  }
  if (state === ScreenState.ErrorUnableToLoadData || portfolioInfoState === ScreenState.ErrorUnableToLoadData) {
    return <UnableToLoadData />;
  }
  return (
    <Container>
      <Box display="flex" flexDirection="column" gap={2} mb={6}>
        <PageHeader title="Asset Allocation" icon={DonutLarge}>
          <PortfolioSelection allowCombining={false} />
        </PageHeader>
        <Box
          display="flex"
          sx={{
            alignItems: { lg: 'center', xl: 'center', md: 'center', sm: 'center', xs: 'flex-start' },
            flexDirection: {
              xl: 'row',
              lg: 'row',
              md: 'row',
              sm: 'row',
              xs: 'column',
            },
          }}
          mt={4}
          mb={4}
          gap={4}
        >
          <PortfolioValue totalValue={totalValue} />
          <LocalizationProvider dateAdapter={AdapterLuxon}>
            <DateRangePicker isDateOnly dateRange={asOfDate} setDateRange={(dateRange: DateRange) => setDate(dateRange)} />
          </LocalizationProvider>
        </Box>
        <Divider />
        <Grid container direction={{ sm: 'column', lg: 'row' }} mt={2} spacing={4} alignItems="center" justifyContent="center">
          <Grid item md={12} lg={6} textAlign="center">
            {!resizing && (
              <DoughnutChart
                width={isBetweenXsAndSm ? '270' : '360'}
                height={isBetweenXsAndSm ? '185' : '345'}
                pieRadius={isBetweenXsAndSm ? '80' : '160'}
                chartData={view.charts}
              />
            )}
          </Grid>
          <Grid item container md={12} lg={6} direction="column" spacing={1}>
            <AssetCardContainer assetCards={view.cards} />
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default AssetAllocation;
